import { esc } from '@presentation/shell/format';

export interface TenantTransferConfirmOptions {
  /** Nom de l’entreprise inscrite dans la sauvegarde. */
  backupTenantName: string;
  /** Nom de l’entreprise du dossier courant (à resaisir pour confirmer). */
  targetTenantName: string;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modale d’alerte pour un transfert d’entreprise explicite (opt-in après mismatch).
 * Jamais proposée par défaut : uniquement après un refus BACKUP_TENANT_MISMATCH.
 */
export function openTenantTransferConfirmDialog(opts: TenantTransferConfirmOptions): void {
  const previouslyFocused =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const backupName = opts.backupTenantName.trim() || 'entreprise inconnue';
  const targetName = opts.targetTenantName.trim();

  const overlay = document.createElement('div');
  overlay.setAttribute('data-tenant-transfer-dialog', '');
  overlay.style.cssText =
    'position:fixed;inset:0;background:rgba(10,18,30,0.65);display:grid;place-items:center;z-index:1200;padding:16px;';

  const dialog = document.createElement('div');
  dialog.setAttribute('role', 'alertdialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-labelledby', 'tenant-transfer-title');
  dialog.setAttribute('aria-describedby', 'tenant-transfer-desc');
  dialog.style.cssText =
    'background:#fff;border-radius:8px;max-width:520px;width:100%;box-shadow:0 16px 40px rgba(0,0,0,0.28);border:2px solid #b42318;outline:none;';
  dialog.tabIndex = -1;

  dialog.innerHTML = `
    <div style="padding:16px 20px;background:#fdecea;border-bottom:1px solid #f5c2c0;border-radius:6px 6px 0 0;">
      <h2 id="tenant-transfer-title" style="margin:0;font-size:1.1rem;color:#b42318;">
        Transfert d’entreprise — action irréversible
      </h2>
    </div>
    <div style="padding:20px 20px 8px;">
      <div id="tenant-transfer-desc" style="line-height:1.5;color:#24292e;">
        <p style="margin:0 0 12px;font-weight:600;color:#b42318;">
          Attention : cette sauvegarde appartient à une autre entreprise
          («&nbsp;${esc(backupName)}&nbsp;»).
        </p>
        <p style="margin:0 0 12px;">
          Un transfert rattache les données de la sauvegarde au dossier courant
          («&nbsp;${esc(targetName || '—')}&nbsp;»). La fiche de l’entreprise ouverte
          n’est pas remplacée. Cette action n’est jamais proposée par défaut.
        </p>
        <p style="margin:0 0 8px;font-size:0.9rem;color:#586069;">
          Pour confirmer, saisissez exactement le nom de l’entreprise cible ci-dessous.
        </p>
      </div>
      <label for="tenant-transfer-confirm-name" style="display:block;font-size:0.85rem;margin:12px 0 4px;font-weight:600;">
        Nom de l’entreprise cible
      </label>
      <input
        id="tenant-transfer-confirm-name"
        type="text"
        autocomplete="off"
        spellcheck="false"
        aria-required="true"
        placeholder="${esc(targetName || 'Nom de l’entreprise')}"
        style="width:100%;padding:10px;border:1px solid #d0d7de;border-radius:4px;font:inherit;box-sizing:border-box;"
      />
      <p id="tenant-transfer-name-error" role="alert" style="display:none;margin:8px 0 0;color:#b42318;font-size:0.85rem;">
        Le nom saisi ne correspond pas à l’entreprise cible.
      </p>
    </div>
    <div style="padding:12px 20px 16px;display:flex;justify-content:flex-end;gap:10px;flex-wrap:wrap;">
      <button type="button" class="btn btn-ghost" data-tenant-transfer-cancel>Annuler</button>
      <button type="button" class="btn btn-danger" data-tenant-transfer-confirm disabled>
        Transférer vers cette entreprise
      </button>
    </div>
  `;

  overlay.appendChild(dialog);
  document.body.appendChild(overlay);

  const nameInput = dialog.querySelector<HTMLInputElement>('#tenant-transfer-confirm-name');
  const nameError = dialog.querySelector<HTMLElement>('#tenant-transfer-name-error');
  const cancelBtn = dialog.querySelector<HTMLButtonElement>('[data-tenant-transfer-cancel]');
  const confirmBtn = dialog.querySelector<HTMLButtonElement>('[data-tenant-transfer-confirm]');

  let settled = false;
  let confirming = false;
  const ac = new AbortController();
  const { signal } = ac;

  const namesMatch = (): boolean => {
    if (!targetName) return false;
    return (nameInput?.value.trim() ?? '') === targetName;
  };

  const syncConfirmEnabled = (): void => {
    if (!confirmBtn) return;
    const ok = namesMatch();
    confirmBtn.disabled = !ok || confirming;
    if (nameError) {
      const showErr = Boolean(nameInput?.value.trim()) && !ok;
      nameError.style.display = showErr ? 'block' : 'none';
    }
  };

  const finish = (confirmed: boolean): void => {
    if (settled) return;
    settled = true;
    ac.abort();
    overlay.remove();
    if (previouslyFocused?.isConnected) previouslyFocused.focus();
    if (confirmed) {
      void Promise.resolve(opts.onConfirm()).catch(() => {
        /* l’appelant gère les erreurs métier */
      });
    } else {
      opts.onCancel?.();
    }
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      finish(false);
      return;
    }
    if (event.key !== 'Tab') return;
    const nodes = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
    if (nodes.length === 0) return;
    const first = nodes[0]!;
    const last = nodes[nodes.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  nameInput?.addEventListener('input', syncConfirmEnabled, { signal });
  cancelBtn?.addEventListener('click', () => finish(false), { signal });
  overlay.addEventListener(
    'click',
    (event) => {
      if (event.target === overlay) finish(false);
    },
    { signal },
  );

  confirmBtn?.addEventListener(
    'click',
    () => {
      if (!namesMatch() || confirming) return;
      confirming = true;
      syncConfirmEnabled();
      if (confirmBtn) confirmBtn.textContent = 'Transfert…';
      finish(true);
    },
    { signal },
  );

  document.addEventListener('keydown', onKeyDown, { capture: true, signal });
  syncConfirmEnabled();
  window.setTimeout(() => {
    nameInput?.focus();
    if (!nameInput) dialog.focus();
  }, 0);
}
